import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency, fmtDateShort } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, FileText } from "lucide-react";

const typeLabel = (t) => (t === "invoice" ? "Quote" : t === "sales_order" ? "Order" : t);

const TypeBadge = ({ t }) => (
  <span
    className={`text-xs px-2 py-0.5 rounded-full border ${
      t === "invoice"
        ? "border-[#1d4ed8]/30 text-[#1d4ed8] bg-[#1d4ed8]/5"
        : "border-[#0f766e]/30 text-[#0f766e] bg-[#0f766e]/5"
    }`}
  >
    {typeLabel(t)}
  </span>
);

export default function ApplianceDocuments() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [dealer, setDealer] = useState("all");
  const [dealers, setDealers] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/appliance/dealers").then(({ data }) => setDealers(data.dealers || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const params = { limit: 200 };
    if (q) params.q = q;
    if (type !== "all") params.document_type = type;
    if (dealer !== "all") params.dealer = dealer;
    const { data } = await api.get("/appliance/documents", { params });
    setData(data);
  }, [q, type, dealer]);

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] animate-fade-in" data-testid="appliance-documents-page">
      <div className="mb-8">
        <div className="label-eyebrow">Appliance Depot</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Documents</h1>
        <p className="text-muted-foreground mt-1">Orders (confirmed) and quotes (pipeline). Search or filter by type and dealer.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="appliance-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search number, customer, dealer…"
            className="pl-9"
          />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-[170px]" data-testid="appliance-type-filter">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="sales_order">Orders</SelectItem>
            <SelectItem value="invoice">Quotes</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dealer} onValueChange={setDealer}>
          <SelectTrigger className="w-[220px]" data-testid="appliance-dealer-filter">
            <SelectValue placeholder="All dealers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All dealers</SelectItem>
            {dealers.map((d) => (
              <SelectItem key={d} value={d}>
                {d}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="surface-card overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-border/70 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          <div className="col-span-2">Number</div>
          <div className="col-span-4">Customer</div>
          <div className="col-span-2">Dealer</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2 text-right">Total</div>
        </div>
        <div className="divide-y divide-border/70">
          {!data && <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div>}
          {data &&
            data.documents.map((d) => (
              <Link
                key={d.id}
                to={`/appliance/documents/${d.id}`}
                data-testid={`appliance-doc-row-${d.id}`}
                className="grid grid-cols-12 gap-3 px-5 py-3 items-center hover:bg-secondary/50 transition-colors"
              >
                <div className="col-span-2 font-mono text-sm">{d.document_number}</div>
                <div className="col-span-4 text-sm truncate">
                  <div className="font-medium truncate">{d.customer || "—"}</div>
                  <div className="text-xs text-muted-foreground">{fmtDateShort(d.issue_date || d.created_date)}</div>
                </div>
                <div className="col-span-2 text-sm truncate text-muted-foreground">{d.dealer_name || "—"}</div>
                <div className="col-span-2">
                  <TypeBadge t={d.document_type} />
                </div>
                <div className="col-span-2 text-right num font-semibold">{fmtCurrency(d.total)}</div>
              </Link>
            ))}
          {data && data.documents.length === 0 && (
            <div className="py-12 text-center text-muted-foreground text-sm flex flex-col items-center gap-2">
              <FileText className="w-6 h-6 opacity-40" />
              No documents match your filters.
            </div>
          )}
        </div>
        {data && (
          <div className="px-5 py-3 border-t border-border/70 text-xs text-muted-foreground">
            Showing {data.documents.length} of {data.total} documents
          </div>
        )}
      </div>
    </div>
  );
}
